import { FC, useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    LinearProgress,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Alert,
    List,
    ListItem,
    ListItemButton,
    TablePagination,
    TableSortLabel,
    Link as MuiLink
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon,
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    Verified as VerifiedIcon,
    PersonAdd as PersonAddIcon,
    Security as SecurityIcon,
    Block as BlockIcon,
    AdminPanelSettings as AdminPanelSettingsIcon,
    PersonRemove as PersonRemoveIcon,
    Apps as AppsIcon
} from '@mui/icons-material';
import { adminApiClient } from '../utils/adminApiClient';
import { TintoaUserSearch } from '@tintoa/ui';
import { useSnackbar } from 'notistack';
import { ICreateUserDto } from '@tintoa/identity-service-client';
import { fetchWithError } from '../../utils/fetchWithError';
import { IUserSession } from "../../../../src/core/types/session/user-session.interface";
import { useNavigate, Link } from 'react-router-dom';
import { AnimatedPasswordCheck } from '../../components/PasswordCheck';
import { isStrongPassword } from '../../../../src/shared';

interface User {
    tid: string;
    profile: {
        firstname: string;
        lastname: string;
        email?: string;
        profileImage?: {
            data: string;
            contentType: string;
            uploadedAt: Date;
        };
    };
    account: {
        locked: boolean;
        verified: boolean;
        roles: Array<{
            id: string;
            name: string;
            ident: string;
        }>;
    };
}

interface UserFormData {
    firstname: string;
    lastname: string;
    email: string;
    password: string;
    clientId?: string;
}

interface UserUpdateFormData {
    firstname: string;
    lastname: string;
}

export const UserManagement: FC = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState<User[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [formData, setFormData] = useState<UserFormData>({
        firstname: '',
        lastname: '',
        email: '',
        password: '',
        clientId: ''
    });
    const [updateFormData, setUpdateFormData] = useState<UserUpdateFormData>({
        firstname: '',
        lastname: ''
    });
    const [errors, setErrors] = useState<Partial<UserFormData>>({});
    const [updateErrors, setUpdateErrors] = useState<Partial<UserUpdateFormData>>({});
    const [currentSession, setCurrentSession] = useState<IUserSession | null>(null);
    const [assignRoleDialogOpen, setAssignRoleDialogOpen] = useState(false);
    const [removeRoleDialogOpen, setRemoveRoleDialogOpen] = useState(false);
    const [systemRoles, setSystemRoles] = useState<Array<{ id: string; name: string; ident: string }>>([]);
    const [loadingSystemRoles, setLoadingSystemRoles] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        loadCurrentSession();
    }, []);

    const loadCurrentSession = async () => {
        try {
            const response = await fetchWithError("/session", {
                credentials: "include"
            });
            setCurrentSession(response.session);
        } catch (error) {
            console.error("Failed to load current session:", error);
        }
    };

    const loadUsers = useCallback(async () => {
        try {
            setLoading(true);
            const result = await adminApiClient.getUsers({
                limit: rowsPerPage,
                offset: page * rowsPerPage,
                sortBy: 'name',
                sortOrder: order
            });
            setUsers(result.data as unknown as User[]);
            setTotal(result.total);
        } catch (error: any) {
            console.error('Failed to load users:', error);
            if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
                enqueueSnackbar('Authentifizierungsfehler: Bitte melden Sie sich erneut an.', { variant: 'error' });
            } else if (error.message?.includes('403') || error.message?.includes('Forbidden')) {
                enqueueSnackbar('Zugriff verweigert: Sie haben keine Admin-Berechtigung.', { variant: 'error' });
            } else {
                enqueueSnackbar(`Fehler beim Laden der Benutzer: ${error.message || 'Unbekannter Fehler'}`, { variant: 'error' });
            }
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, order, enqueueSnackbar]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSortByName = () => {
        setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        setPage(0);
    };

    const handleOpenDialog = (user?: User) => {
        if (user) {
            setEditingUser(user);
            setUpdateFormData({
                firstname: user.profile.firstname,
                lastname: user.profile.lastname
            });
        } else {
            setEditingUser(null);
            setFormData({
                firstname: '',
                lastname: '',
                email: '',
                password: '',
                clientId: ''
            });
        }
        setErrors({});
        setUpdateErrors({});
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingUser(null);
        setFormData({
            firstname: '',
            lastname: '',
            email: '',
            password: '',
            clientId: ''
        });
        setUpdateFormData({
            firstname: '',
            lastname: ''
        });
        setErrors({});
        setUpdateErrors({});
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
        setAnchorEl(event.currentTarget);
        setSelectedUser(user);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedUser(null);
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<UserFormData> = {};
        
        if (!formData.firstname.trim()) {
            newErrors.firstname = 'Vorname ist erforderlich';
        }
        
        if (!formData.lastname.trim()) {
            newErrors.lastname = 'Nachname ist erforderlich';
        }
        
        if (!formData.email.trim()) {
            newErrors.email = 'E-Mail ist erforderlich';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Ungültige E-Mail-Adresse';
        }

        if (!formData.password.trim()) {
            newErrors.password = 'Passwort ist erforderlich';
        } else if (!isStrongPassword(formData.password).isStrongPassword) {
            newErrors.password = 'Passwort erfüllt nicht alle Kriterien (Groß-/Kleinbuchstabe, Zahl, Sonderzeichen, mind. 8 Zeichen).';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateUpdateForm = (): boolean => {
        const newErrors: Partial<UserUpdateFormData> = {};
        
        if (!updateFormData.firstname.trim()) {
            newErrors.firstname = 'Vorname ist erforderlich';
        }
        
        if (!updateFormData.lastname.trim()) {
            newErrors.lastname = 'Nachname ist erforderlich';
        }

        setUpdateErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (editingUser) {
            if (!validateUpdateForm()) return;
            
            try {
                await adminApiClient.updateUserProfile(editingUser.tid, updateFormData);
                enqueueSnackbar('Benutzer erfolgreich aktualisiert', { variant: 'success' });
                handleCloseDialog();
                loadUsers();
            } catch (error) {
                console.error('Failed to update user:', error);
                enqueueSnackbar('Fehler beim Aktualisieren des Benutzers', { variant: 'error' });
            }
        } else {
            if (!validateForm()) return;
            
            try {
                await adminApiClient.createUser(formData as ICreateUserDto);
                enqueueSnackbar('Benutzer erfolgreich erstellt', { variant: 'success' });
                handleCloseDialog();
                loadUsers();
            } catch (error) {
                console.error('Failed to create user:', error);
                enqueueSnackbar(error instanceof Error ? error.message : 'Fehler beim Erstellen des Benutzers', { variant: 'error' });
            }
        }
    };

    const handleDelete = async () => {
        if (!selectedUser) return;
        
        if (window.confirm(`Möchten Sie den Benutzer "${selectedUser.profile.firstname} ${selectedUser.profile.lastname}" wirklich löschen?`)) {
            try {
                await adminApiClient.deleteUser(selectedUser.tid);
                enqueueSnackbar('Benutzer erfolgreich gelöscht', { variant: 'success' });
                loadUsers();
            } catch (error) {
                console.error('Failed to delete user:', error);
                enqueueSnackbar('Fehler beim Löschen des Benutzers', { variant: 'error' });
            }
        }
        handleMenuClose();
    };

    const handleToggleLock = async () => {
        if (!selectedUser) return;
        
        try {
            if (selectedUser.account.locked) {
                await adminApiClient.unlockUser(selectedUser.tid);
                enqueueSnackbar('Benutzer entsperrt', { variant: 'success' });
            } else {
                await adminApiClient.lockUser(selectedUser.tid);
                enqueueSnackbar('Benutzer gesperrt', { variant: 'success' });
            }
            loadUsers();
        } catch (error) {
            console.error('Failed to toggle lock:', error);
            enqueueSnackbar('Fehler beim Ändern des Sperrstatus', { variant: 'error' });
        }
        handleMenuClose();
    };

    const handleToggleVerify = async () => {
        if (!selectedUser) return;
        
        try {
            if (!selectedUser.account.verified) {
                await adminApiClient.verifyUser(selectedUser.tid);
                enqueueSnackbar('Benutzer verifiziert', { variant: 'success' });
            }
            loadUsers();
        } catch (error) {
            console.error('Failed to verify user:', error);
            enqueueSnackbar('Fehler beim Verifizieren des Benutzers', { variant: 'error' });
        }
        handleMenuClose();
    };

    const loadSystemRoles = async () => {
        try {
            setLoadingSystemRoles(true);
            const roles = await adminApiClient.getRoles();
            setSystemRoles(roles);
        } catch (error) {
            console.error('Failed to load system roles:', error);
            enqueueSnackbar('Fehler beim Laden der System-Rollen', { variant: 'error' });
        } finally {
            setLoadingSystemRoles(false);
        }
    };

    const handleAssignSystemRole = () => {
        if (!selectedUser) return;
        loadSystemRoles();
        setAssignRoleDialogOpen(true);
        handleMenuClose();
    };

    const handleRemoveSystemRole = () => {
        if (!selectedUser) return;
        loadSystemRoles();
        setRemoveRoleDialogOpen(true);
        handleMenuClose();
    };

    const handleAssignRole = async (roleIdent: string) => {
        if (!selectedUser) return;
        
        try {
            await adminApiClient.addUserRole(selectedUser.tid, roleIdent);
            enqueueSnackbar('System-Rolle erfolgreich zugewiesen', { variant: 'success' });
            setAssignRoleDialogOpen(false);
            loadUsers();
        } catch (error) {
            console.error('Failed to assign role:', error);
            enqueueSnackbar('Fehler beim Zuweisen der System-Rolle', { variant: 'error' });
        }
    };

    const handleRemoveRole = async (roleIdent: string) => {
        if (!selectedUser) return;
        
        try {
            await adminApiClient.removeUserRole(selectedUser.tid, roleIdent);
            enqueueSnackbar('System-Rolle erfolgreich entfernt', { variant: 'success' });
            setRemoveRoleDialogOpen(false);
            loadUsers();
        } catch (error) {
            console.error('Failed to remove role:', error);
            enqueueSnackbar('Fehler beim Entfernen der System-Rolle', { variant: 'error' });
        }
    };

    const handleCloseAssignRoleDialog = () => {
        setAssignRoleDialogOpen(false);
    };

    const handleCloseRemoveRoleDialog = () => {
        setRemoveRoleDialogOpen(false);
    };

    const getAvailableRoles = () => {
        if (!selectedUser) return [];
        const assignedRoleIdents = selectedUser.account.roles.map(role => role.ident);
        return systemRoles.filter(role => !assignedRoleIdents.includes(role.ident));
    };

    const getAssignedSystemRoles = () => {
        if (!selectedUser) return [];
        return selectedUser.account.roles.filter(role => 
            systemRoles.some(sr => sr.ident === role.ident)
        );
    };

    const handlePrivilegeUser = async () => {
        if (!selectedUser) return;
        
        try {
            await adminApiClient.privilegeUser(selectedUser.tid);
            enqueueSnackbar('Benutzer privilegiert (Admin-Rolle hinzugefügt)', { variant: 'success' });
            loadUsers();
        } catch (error) {
            console.error('Failed to privilege user:', error);
            enqueueSnackbar('Fehler beim Privilegieren des Benutzers', { variant: 'error' });
        }
        handleMenuClose();
    };

    const handleDegradeUser = async () => {
        if (!selectedUser) return;
        
        try {
            await adminApiClient.degradeUser(selectedUser.tid);
            enqueueSnackbar('Benutzer degradiert (Admin-Rolle entfernt)', { variant: 'success' });
            loadUsers();
        } catch (error) {
            console.error('Failed to degrade user:', error);
            enqueueSnackbar('Fehler beim Degradieren des Benutzers', { variant: 'error' });
        }
        handleMenuClose();
    };


    const isCurrentUser = (user: User): boolean => {
        return currentSession?.tid === user.tid;
    };

    // "Nachname, Vorname" to match the sort order; falls back to just the
    // first name when no last name is set.
    const formatUserName = (user: User): string => {
        const { firstname, lastname } = user.profile;
        return lastname ? `${lastname}, ${firstname}` : firstname;
    };

    const getStatusChips = (user: User) => {
        const chips = [];
        
        if (user.account.verified) {
            chips.push(
                <Chip
                    key="verified"
                    icon={<VerifiedIcon />}
                    label="Verifiziert"
                    color="success"
                    size="small"
                    sx={{ mr: 1 }}
                />
            );
        } else {
            chips.push(
                <Chip
                    key="unverified"
                    label="Nicht verifiziert"
                    color="warning"
                    size="small"
                    sx={{ mr: 1 }}
                />
            );
        }
        
        if (user.account.locked) {
            chips.push(
                <Chip
                    key="locked"
                    icon={<LockIcon />}
                    label="Gesperrt"
                    color="error"
                    size="small"
                    sx={{ mr: 1 }}
                />
            );
        }
        
        const isAdmin = user.account.roles.some(role => role.ident === 'Admin');
        if (isAdmin) {
            chips.push(
                <Chip
                    key="admin"
                    icon={<SecurityIcon />}
                    label="Admin"
                    color="primary"
                    size="small"
                />
            );
        }
        
        return chips;
    };

    if (loading && users.length === 0) {
        return (
            <Box sx={{ width: '100%' }}>
                <LinearProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Benutzerverwaltung
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Verwalten Sie Benutzerkonten, Berechtigungen und Status
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Neuer Benutzer
                </Button>
            </Box>

            {/* Global user search (jump to profile) */}
            <Box sx={{ maxWidth: 480, mb: 3 }}>
                <TintoaUserSearch
                    placeholder="Benutzer global suchen…"
                    onSelect={(user) =>
                        navigate(`/app/admin/users/${user.tid}`)
                    }
                />
            </Box>

            {/* Users Table */}
            <Paper variant="subtle" sx={{ width: '100%', overflow: 'hidden' }}>
                {loading && <LinearProgress />}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sortDirection={order}>
                                    <TableSortLabel
                                        active
                                        direction={order}
                                        onClick={handleSortByName}
                                    >
                                        Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>TID</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Rollen</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow 
                                    key={user.tid} 
                                    hover
                                    sx={{
                                        bgcolor: isCurrentUser(user) ? 'action.hover' : 'inherit',
                                        '&:hover': {
                                            bgcolor: isCurrentUser(user) ? 'action.selected' : 'action.hover'
                                        }
                                    }}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <MuiLink
                                                component={Link}
                                                to={`/app/admin/users/${user.tid}`}
                                                sx={{ 
                                                    textDecoration: 'none',
                                                    color: 'inherit',
                                                    '&:hover': { textDecoration: 'underline' }
                                                }}
                                            >
                                                <Typography variant="subtitle2" fontWeight="medium">
                                                    {formatUserName(user)}
                                                </Typography>
                                            </MuiLink>
                                            {isCurrentUser(user) && (
                                                <Chip
                                                    label="Aktueller Benutzer"
                                                    size="small"
                                                    color="default"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                            {user.tid}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {getStatusChips(user)}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {user.account.roles.map((role) => (
                                                <Chip
                                                    key={role.id}
                                                    label={role.name}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="inherit"
                                            component={Link}
                                            to={`/app/admin/users/${user.tid}`}
                                            startIcon={<EditIcon />}
                                        >
                                            Details
                                        </Button>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleMenuOpen(e, user)}
                                            sx={{ ml: 1 }}
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                {users.length === 0 && !loading && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                            Keine Benutzer gefunden.
                        </Typography>
                    </Box>
                )}

                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Zeilen pro Seite:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}–${to} von ${count}`
                    }
                />
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingUser ? 'Benutzer bearbeiten' : 'Neuen Benutzer erstellen'}
                </DialogTitle>
                <DialogContent>
                    <Box
                        component="form"
                        action="#"
                        onSubmit={(e) => e.preventDefault()}
                        sx={{ pt: 2 }}
                        noValidate
                    >
                        {editingUser ? (
                            // Edit form
                            <>
                                <TextField
                                    fullWidth
                                    label="Vorname"
                                    value={updateFormData.firstname}
                                    onChange={(e) => setUpdateFormData({ ...updateFormData, firstname: e.target.value })}
                                    error={!!updateErrors.firstname}
                                    helperText={updateErrors.firstname}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="Nachname"
                                    value={updateFormData.lastname}
                                    onChange={(e) => setUpdateFormData({ ...updateFormData, lastname: e.target.value })}
                                    error={!!updateErrors.lastname}
                                    helperText={updateErrors.lastname}
                                    sx={{ mb: 2 }}
                                />
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    E-Mail und Passwort können nicht über diese Oberfläche geändert werden.
                                </Alert>
                            </>
                        ) : (
                            // Create form
                            <>
                                <TextField
                                    fullWidth
                                    label="Vorname"
                                    value={formData.firstname}
                                    onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
                                    error={!!errors.firstname}
                                    helperText={errors.firstname}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="Nachname"
                                    value={formData.lastname}
                                    onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
                                    error={!!errors.lastname}
                                    helperText={errors.lastname}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="E-Mail"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="Passwort"
                                    type="password"
                                    name="newPassword"
                                    autoComplete="new-password"
                                    inputProps={{
                                        'data-form-type': 'other',
                                        'data-lpignore': 'true',
                                        'data-1p-ignore': true
                                    }}
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    error={!!errors.password}
                                    helperText={errors.password}
                                    sx={{ mb: 2 }}
                                />
                                <Box sx={{ mb: 2 }}>
                                    <AnimatedPasswordCheck
                                        show={formData.password.length > 0 && !isStrongPassword(formData.password).isStrongPassword}
                                        password={formData.password}
                                    />
                                </Box>
                                <TextField
                                    fullWidth
                                    label="Client ID (optional)"
                                    value={formData.clientId || ''}
                                    onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                                    helperText="Für App-spezifische Benutzer"
                                    sx={{ mb: 2 }}
                                />
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Abbrechen</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {editingUser ? 'Aktualisieren' : 'Erstellen'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {/* Lock/Unlock - Disabled for current user */}
                <MenuItem 
                    onClick={handleToggleLock}
                    disabled={selectedUser ? isCurrentUser(selectedUser) : false}
                >
                    <ListItemIcon>
                        {selectedUser?.account.locked ? <LockOpenIcon /> : <LockIcon />}
                    </ListItemIcon>
                    <ListItemText>
                        {selectedUser?.account.locked ? 'Entsperren' : 'Sperren'}
                        {selectedUser && isCurrentUser(selectedUser) && ' (Nicht für eigenen Account)'}
                    </ListItemText>
                </MenuItem>
                
                {/* Verify - Disabled for current user */}
                {!selectedUser?.account.verified && (
                    <MenuItem 
                        onClick={handleToggleVerify}
                        disabled={selectedUser ? isCurrentUser(selectedUser) : false}
                    >
                        <ListItemIcon>
                            <VerifiedIcon />
                        </ListItemIcon>
                        <ListItemText>
                            Verifizieren
                            {selectedUser && isCurrentUser(selectedUser) && ' (Nicht für eigenen Account)'}
                        </ListItemText>
                    </MenuItem>
                )}
                
                {/* System Role Management */}
                <MenuItem 
                    onClick={handleAssignSystemRole}
                    disabled={selectedUser ? isCurrentUser(selectedUser) : false}
                >
                    <ListItemIcon>
                        <PersonAddIcon />
                    </ListItemIcon>
                    <ListItemText>
                        System-Rolle zuweisen
                        {selectedUser && isCurrentUser(selectedUser) && ' (Nicht für eigenen Account)'}
                    </ListItemText>
                </MenuItem>
                <MenuItem 
                    onClick={handleRemoveSystemRole}
                    disabled={selectedUser ? isCurrentUser(selectedUser) : false}
                >
                    <ListItemIcon>
                        <BlockIcon />
                    </ListItemIcon>
                    <ListItemText>
                        System-Rolle entfernen
                        {selectedUser && isCurrentUser(selectedUser) && ' (Nicht für eigenen Account)'}
                    </ListItemText>
                </MenuItem>
                
                {/* Admin Role Management - Disabled for current user */}
                {!selectedUser?.account.roles.some(role => role.ident === 'Admin' || role.ident === 'tnt:ids_admin') ? (
                    <MenuItem 
                        onClick={handlePrivilegeUser}
                        disabled={selectedUser ? isCurrentUser(selectedUser) : false}
                    >
                        <ListItemIcon>
                            <AdminPanelSettingsIcon />
                        </ListItemIcon>
                        <ListItemText>
                            Benutzer privilegieren
                            {selectedUser && isCurrentUser(selectedUser) && ' (Nicht für eigenen Account)'}
                        </ListItemText>
                    </MenuItem>
                ) : (
                    <MenuItem 
                        onClick={handleDegradeUser}
                        disabled={selectedUser ? isCurrentUser(selectedUser) : false}
                    >
                        <ListItemIcon>
                            <PersonRemoveIcon />
                        </ListItemIcon>
                        <ListItemText>
                            Benutzer degradieren
                            {selectedUser && isCurrentUser(selectedUser) && ' (Nicht für eigenen Account)'}
                        </ListItemText>
                    </MenuItem>
                )}
                
                {/* App Users - Always available */}
                <MenuItem 
                    onClick={() => {
                        if (selectedUser) {
                            navigate(`/app/admin/users/${selectedUser.tid}`);
                        }
                        handleMenuClose();
                    }}
                >
                    <ListItemIcon>
                        <AppsIcon />
                    </ListItemIcon>
                    <ListItemText>App-Benutzer anzeigen</ListItemText>
                </MenuItem>
                
                {/* Delete - Disabled for current user */}
                <MenuItem 
                    onClick={handleDelete} 
                    disabled={selectedUser ? isCurrentUser(selectedUser) : false}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <DeleteIcon color="error" />
                    </ListItemIcon>
                    <ListItemText>
                        Löschen
                        {selectedUser && isCurrentUser(selectedUser) && ' (Nicht für eigenen Account)'}
                    </ListItemText>
                </MenuItem>
            </Menu>

            {/* Assign System Role Dialog */}
            <Dialog 
                open={assignRoleDialogOpen} 
                onClose={handleCloseAssignRoleDialog} 
                maxWidth="sm" 
                fullWidth
            >
                <DialogTitle>
                    System-Rolle zuweisen für {selectedUser?.profile.firstname} {selectedUser?.profile.lastname}
                </DialogTitle>
                <DialogContent>
                    {loadingSystemRoles ? (
                        <Box sx={{ p: 2 }}>
                            <LinearProgress />
                        </Box>
                    ) : (
                        <Box sx={{ pt: 2 }}>
                            {getAvailableRoles().length === 0 ? (
                                <Alert severity="info">
                                    Alle verfügbaren System-Rollen sind bereits zugewiesen.
                                </Alert>
                            ) : (
                                <List>
                                    {getAvailableRoles().map((role) => (
                                        <ListItem key={role.id} disablePadding>
                                            <ListItemButton onClick={() => handleAssignRole(role.ident)}>
                                                <ListItemText
                                                    primary={role.name}
                                                    secondary={role.ident}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAssignRoleDialog}>Abbrechen</Button>
                </DialogActions>
            </Dialog>

            {/* Remove System Role Dialog */}
            <Dialog 
                open={removeRoleDialogOpen} 
                onClose={handleCloseRemoveRoleDialog} 
                maxWidth="sm" 
                fullWidth
            >
                <DialogTitle>
                    System-Rolle entfernen von {selectedUser?.profile.firstname} {selectedUser?.profile.lastname}
                </DialogTitle>
                <DialogContent>
                    {loadingSystemRoles ? (
                        <Box sx={{ p: 2 }}>
                            <LinearProgress />
                        </Box>
                    ) : (
                        <Box sx={{ pt: 2 }}>
                            {getAssignedSystemRoles().length === 0 ? (
                                <Alert severity="info">
                                    Keine System-Rollen zugewiesen.
                                </Alert>
                            ) : (
                                <List>
                                    {getAssignedSystemRoles().map((role) => (
                                        <ListItem key={role.id} disablePadding>
                                            <ListItemButton onClick={() => handleRemoveRole(role.ident)}>
                                                <ListItemText
                                                    primary={role.name}
                                                    secondary={role.ident}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseRemoveRoleDialog}>Abbrechen</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};
